import { deriveCameraGraphNavigationPhase } from '@toolproof-core/visualization/_pure/camera-graph';
import type { CameraGraph } from '@toolproof-core/visualization/_contract/camera';
import type { SpaceDeriver } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { conceptPresentationAt, planAnchorIndex } from '@/_pure/episode';
import { resolveTraceEpisodeCameraPolicy, selectEpisodeArrivalCameraView, selectEpisodeCompletionCameraView } from '@/_pure/episodeCamera';
import { selectTraceEpisodePresentation } from '@/_pure/episodePresentation';
import { deriveBooleanCameraSteps } from '@/adapters/primitive/_pure/derivation/boolean/cameraSteps';
import {
    buildBooleanAssignedWheels,
    buildBooleanDraftRod,
    deriveBooleanAssignmentStep,
} from '@/adapters/primitive/_pure/derivation/boolean/traceProjection';
import { deriveNaturalCameraChoreography, withNaturalCameraChoreography } from '@/adapters/primitive/_pure/derivation/natural/cameraChoreography';
import { toPrimitiveCameraConfig, toPrimitiveCameraViewId, withPrimitiveCameraDestination } from '@/adapters/primitive/_pure/derivation/cameraGraph';
import { resolvePrimitivePresentationCameraView } from '@/adapters/primitive/_pure/derivation/episodeCamera';
import { derivePrimitiveConceptGuidance, derivePrimitiveEpisodeGuidance } from '@/adapters/primitive/_pure/derivation/episodeGuidance';
import { primitiveConceptLayoutKey } from '@/adapters/primitive/_pure/episode';
import { buildPrimitiveRodFrame } from '@/adapters/primitive/_pure/derivation/primitiveRod';
import { derivePrimitiveStepTransitionPhases, reversePrimitiveStepStates } from '@/adapters/primitive/_pure/derivation/primitiveStepTransitions';
import { configForPrimitiveTrace, toPrimitiveStepWheels } from '@/adapters/primitive/_pure/derivation/primitiveSteps';
import { buildPrimitiveOperationDraftWheels } from '@/adapters/primitive/_pure/derivation/wheels';
import { resolvePrimitiveOperationInput } from '@/adapters/primitive/_pure/interaction/operationInput';
import { createPrimitiveScenePlanResolver, resolvePrimitiveEpisode } from '@/adapters/primitive/episodeCatalog';
import { derivePrimitiveCameraGraph } from '@/adapters/primitive/primitiveCameraPlan';
import { createPrimitiveStepResolver } from '@/adapters/primitive/traceResolver';
import type {
    Config,
    CosmosDataModel,
} from '@/adapters/primitive/_contract/config';
import type { RuntimeState } from '@/adapters/primitive/_contract/config';

export function createSpaceDeriver(): SpaceDeriver<
    CosmosDataModel,
    Config,
    RuntimeState
> {
    const resolveSteps = createPrimitiveStepResolver();
    const resolveScenePlan = createPrimitiveScenePlanResolver();
    let traceConfigCache: {
        config: Config;
        trace: RuntimeState['selectedTrace'];
        effective: Config;
    } | undefined;
    let booleanPlanCache: {
        config: Config;
        spaceKey: Parameters<typeof derivePrimitiveCameraGraph>[0]['spaceKey'];
        operation: RuntimeState['inputDraft']['operation'];
        plan: ReturnType<typeof derivePrimitiveCameraGraph>;
    } | undefined;
    // Step boundaries re-run this derivation; steps, camera graph and choreography only change with the operation input.
    let operationCache: {
        config: Config;
        spaceKey: Parameters<typeof derivePrimitiveCameraGraph>[0]['spaceKey'];
        inputKey: string;
        steps: ReturnType<typeof resolveSteps>;
        plan?: ReturnType<typeof derivePrimitiveCameraGraph>;
        choreography?: ReturnType<typeof deriveNaturalCameraChoreography>;
        growthGraph?: CameraGraph;
    } | undefined;
    return (input) => {
        const { spaceKey, runtimeState } = input;
        const steps = resolveSteps(input.config, runtimeState);
        if (traceConfigCache?.config !== input.config || traceConfigCache.trace !== runtimeState.selectedTrace) {
            traceConfigCache = { config: input.config, trace: runtimeState.selectedTrace,
                effective: configForPrimitiveTrace(input.config, runtimeState.selectedTrace) };
        }
        const config = traceConfigCache.effective;
        const operationInput = resolvePrimitiveOperationInput(runtimeState.inputDraft);
        const inputKey = JSON.stringify({ operationInput, trace: runtimeState.selectedTrace?.handle });
        if (operationCache?.config !== input.config || operationCache.spaceKey !== spaceKey || operationCache.inputKey !== inputKey
            || operationCache.steps !== steps) {
            operationCache = {
                config: input.config, spaceKey, inputKey, steps,
            };
        }
        const cache = operationCache;
        const committedStep = steps[runtimeState.committedStepIndex] ?? steps[0];
        const pendingStep = runtimeState.pendingStepIndex === null
            ? undefined
            : steps[runtimeState.pendingStepIndex];
        const draft = runtimeState.inputDraft;
        const assignment = runtimeState.booleanAssignment;
        const visibleDraft = assignment?.previousDraft ?? draft;
        const draftBooleanRod = buildBooleanDraftRod(config, visibleDraft);
        const previewDraft = runtimeState.selectedTrace === undefined && draftBooleanRod !== undefined && runtimeState.committedStepIndex === 0;
        const committedFrame = committedStep?.keyframes.at(-1);
        const resolvedPlan = runtimeState.episode && !runtimeState.conceptArrangement
            ? resolveScenePlan(config, runtimeState.episode.episodeHandle, runtimeState.sceneIndex ?? 0) : undefined;
        const resolvedConcept = runtimeState.episode && runtimeState.conceptArrangement
            ? resolvePrimitiveEpisode(runtimeState.episode.episodeHandle, runtimeState.sceneIndex ?? 0) : undefined;
        const conceptBeat = resolvedConcept?.scene.sceneKind === 'concept'
            ? resolvedConcept.scene.beats[runtimeState.episode!.beatIndex] : undefined;
        const episodeCameraView = resolvedPlan && runtimeState.episode
            ? selectTraceEpisodePresentation({ ...resolvedPlan, playback: runtimeState.episode,
                committedStepIndex: runtimeState.committedStepIndex })?.selected.cameraView
            : conceptBeat && runtimeState.episode && !runtimeState.episode.error
                ? conceptPresentationAt(conceptBeat, runtimeState.episode).segment?.cameraView : undefined;
        const labelConfig = episodeCameraView === 'wheel-labels' ? {
            ...config, base: { ...config.base, display: { ...config.base.display, label: {
                ...config.base.display.label, aboveItemStyleOverride: {
                    ...config.base.display.label.aboveItemStyleOverride,
                    fontSize: (config.base.display.label.aboveItemStyleOverride?.fontSize
                        ?? config.base.display.label.wheelTitleFontSize) * 3,
                },
            } } },
        } : config;
        const draftWheels = draftBooleanRod === undefined
            ? buildPrimitiveOperationDraftWheels({ config, draft: visibleDraft })
            : buildBooleanAssignedWheels(config, visibleDraft);
        const geometryBase = buildPrimitiveRodFrame({
            spaceKey,
            config: labelConfig,
            wheels: previewDraft || committedStep === undefined
                ? draftWheels
                : toPrimitiveStepWheels(committedStep),
            ...((previewDraft ? draftBooleanRod : committedFrame?.booleanRod) === undefined
                ? {}
                : { booleanRod: previewDraft ? draftBooleanRod! : committedFrame!.booleanRod! }),
        });
        const camera = toPrimitiveCameraConfig(config);
        const navigationMode = (resolvedPlan ?? resolvedConcept)?.episode.cameraNavigationMode;
        if (navigationMode) {
            camera.base = { ...camera.base, navigationMode };
        }
        const scope = 'operation';
        let cameraSteps = steps.length > 0 ? steps : [{
            stepKey: 'draft', label: 'Draft', description: 'Operation draft',
            keyframes: [{ wheels: draftWheels, ...(draftBooleanRod ? { booleanRod: draftBooleanRod } : {}),
                transition: 'instant' as const, events: [] }],
        }];
        if (runtimeState.episode && runtimeState.conceptArrangement?.arrangementKind === 'natural-wheel') {
            const { scene } = resolvePrimitiveEpisode(runtimeState.episode.episodeHandle, runtimeState.sceneIndex ?? 0);
            if (scene.sceneKind !== 'concept') throw new Error('Concept camera lost its concept scene.');
            const layoutKey = primitiveConceptLayoutKey(runtimeState.conceptArrangement);
            const arrangements = [runtimeState.conceptArrangement, ...scene.beats.flatMap(beat => [beat.arrangement,
                ...(beat.presentation ?? []).flatMap(segment => segment.arrangement ? [segment.arrangement] : [])])]
                .filter(arrangement => arrangement.arrangementKind === 'natural-wheel')
                .filter(arrangement => primitiveConceptLayoutKey(arrangement) === layoutKey);
            const sweep = arrangements.flatMap(arrangement => arrangement.bandLabels?.sweep ? [arrangement.bandLabels.sweep] : [])[0];
            const bandLabels = {
                radial: arrangements.some(arrangement =>
                    arrangement.bandLabels?.radial ?? config.natural.display.label.radialBandLabelsVisible),
                angular: arrangements.some(arrangement =>
                    arrangement.bandLabels?.angular ?? config.natural.display.label.angularBandLabelsVisible),
                ...(sweep ? { sweep: { ...sweep, powers: runtimeState.conceptArrangement.powers } } : {}),
            };
            const value = Math.max(...arrangements.map(arrangement => arrangement.value));
            // Reserve the full scene's key from entry, even while its labels are hidden.
            cameraSteps = cameraSteps.map(step => ({ ...step, keyframes: step.keyframes.map(keyframe => ({
                ...keyframe, wheels: keyframe.wheels.map(wheel => wheel.valueKind === undefined || wheel.valueKind === 'natural'
                    ? { ...wheel, value, bandLabels } : wheel),
            })) }));
        }
        if (runtimeState.conceptArrangement?.arrangementKind === 'natural-bands'
            && runtimeState.conceptArrangement.bandKind !== 'equivalence') {
            // Reserve the wider value flag and angular labels before their cues to avoid reframing.
            cameraSteps = cameraSteps.map(step => ({ ...step, keyframes: step.keyframes.map(keyframe => ({
                ...keyframe, wheels: keyframe.wheels.map(wheel => ({
                    ...wheel, valueKind: 'natural' as const, value: 10 ** (Math.max(...wheel.powers) + 1),
                    bandLabels: { radial: true, angular: true },
                })),
            })) }));
        }
        if (runtimeState.conceptArrangement?.arrangementKind === 'natural-bands'
            && runtimeState.conceptArrangement.bandKind === 'equivalence') {
            // Comparisons reserve both complete wheels so changing powers never reframes them.
            cameraSteps = cameraSteps.map(step => ({ ...step, keyframes: step.keyframes.map(keyframe => ({
                ...keyframe, wheels: keyframe.wheels.map(wheel => ({
                    role: wheel.role, position: wheel.position, roleless: true,
                    valueKind: 'natural' as const, value: 123, powers: [0, 1, 2],
                    bandLabels: { radial: true, angular: true,
                        sweep: { afterCue: 'reference', powers: [0, 1, 2], durationMs: 2000 } },
                })),
            })) }));
        }
        let plan: ReturnType<typeof derivePrimitiveCameraGraph>;
        const growthSteps = steps.filter(step => step.illustrativeQuotientPower !== undefined);
        if (growthSteps.length > 0) {
            const firstGrowth = steps.indexOf(growthSteps[0]!);
            const lastExecuted = steps[firstGrowth - 1];
            if (!lastExecuted) throw new Error('Quotient illustration requires an executed starting state.');
            // Large illustrative wheels must not widen the opening lesson's camera envelope.
            cameraSteps = cameraSteps.map((step, index) => index < firstGrowth ? step : {
                ...lastExecuted, moment: step.moment, stepKey: step.stepKey,
            });
            if (!cache.growthGraph) {
                const stations: CameraGraph['stations'] = {};
                const views: CameraGraph['views'] = {};
                for (const step of growthSteps) {
                    const expansion = step.keyframes.findIndex(frame => frame.wheels.some(wheel =>
                        wheel.role === 'quotient' && wheel.powers.includes(step.illustrativeQuotientPower!)));
                    for (const opening of [true, false]) {
                        const fitted = derivePrimitiveCameraGraph({ spaceKey, config, camera, scope, fullOverview: true,
                            steps: [{ ...step, cameraFocus: { stationKey: 'overview', viewKey: 'overview',
                                roles: ['dividend', 'divisor', 'remainder', 'quotient'] },
                                keyframes: opening ? step.keyframes.slice(0, expansion) : step.keyframes }] }).graph;
                        const id = toPrimitiveCameraViewId(scope,
                            `illustrative-quotient:${step.illustrativeQuotientPower}${opening ? ':opening' : ''}`);
                        const overview = toPrimitiveCameraViewId(scope, 'overview');
                        stations[id] = fitted.stations[overview]!;
                        views[id] = { ...fitted.views[overview]!, station: id };
                    }
                }
                cache.growthGraph = { stations, views, paths: [] };
            }
        }
        if (draftBooleanRod !== undefined) {
            if (booleanPlanCache?.config !== config || booleanPlanCache.spaceKey !== spaceKey
                || booleanPlanCache.operation !== draft.operation) {
                const booleanSteps = deriveBooleanCameraSteps(config, draft.operation);
                if (!booleanSteps) throw new Error(`Missing Boolean camera envelope for ${draft.operation}.`);
                booleanPlanCache = { config, spaceKey, operation: draft.operation,
                    plan: derivePrimitiveCameraGraph({ spaceKey, config, camera, scope, steps: booleanSteps }) };
            }
            plan = booleanPlanCache.plan;
        } else if (steps.length > 0) {
            cache.plan ??= derivePrimitiveCameraGraph({ spaceKey, config, camera, scope, steps: cameraSteps });
            plan = cache.plan;
        } else {
            plan = derivePrimitiveCameraGraph({ spaceKey, config, camera, scope, steps: cameraSteps });
        }
        const overviewId = toPrimitiveCameraViewId(scope, 'overview');
        const overviewStation = plan.graph.stations[overviewId];
        const overviewView = plan.graph.views[overviewId];
        if (!overviewStation || !overviewView) throw new Error('Primitive camera requires an operation overview.');
        const conceptView = runtimeState.conceptArrangement?.arrangementKind === 'natural-bands'
            ? runtimeState.conceptArrangement.bandKind === 'equivalence' ? 'bands-comparison'
                : `bands-single-${runtimeState.conceptArrangement.power}` : undefined;
        let graph: CameraGraph = {
            ...plan.graph,
            stations: { ...plan.graph.stations, overview: overviewStation },
            views: { ...plan.graph.views, overview: { ...overviewView, station: 'overview' },
                ...(conceptView ? { [conceptView]: { ...overviewView, station: 'overview' } } : {}) },
        };
        const naturalArithmetic = ['addition', 'subtraction', 'multiplication', 'division'].includes(draft.operation);
        const deriveChoreography = () => deriveNaturalCameraChoreography({
            spaceKey, config, scope, steps: growthSteps.length === 0 ? steps : steps.map(step =>
                step.illustrativeQuotientPower === undefined ? step : {
                    ...step, keyframes: step.keyframes.map(frame => ({ ...frame, events: [] })),
                }), graph, viewByStepIndex: plan.viewByStepIndex,
            cameraCatalog: runtimeState.episode ? runtimeState.sceneDirectionCatalogs?.camera : undefined,
        });
        const choreography = !naturalArithmetic ? undefined
            : plan === cache.plan ? (cache.choreography ??= deriveChoreography())
                : deriveChoreography();
        if (choreography) graph = choreography.graph;
        if (cache.growthGraph) graph = { ...graph,
            stations: { ...graph.stations, ...cache.growthGraph.stations },
            views: { ...graph.views, ...cache.growthGraph.views },
        };
        const cameraPolicy = resolvedPlan ? resolveTraceEpisodeCameraPolicy(resolvedPlan.scene) : undefined;
        const authoredView = episodeCameraView !== undefined && episodeCameraView === conceptView ? conceptView
            : resolvePrimitivePresentationCameraView(episodeCameraView, scope, graph);
        const completedView = selectEpisodeCompletionCameraView({
            atCompletion: runtimeState.committedStepIndex === steps.length - 1, policy: cameraPolicy, authoredView,
        });
        const growthView = committedStep?.illustrativeQuotientPower === undefined ? undefined
            : toPrimitiveCameraViewId(scope, `illustrative-quotient:${committedStep.illustrativeQuotientPower}`);
        const activeView = (pendingStep === undefined ? growthView ?? authoredView : undefined) ?? conceptView ?? (assignment !== undefined || runtimeState.inputEditing
            || (choreography !== undefined && pendingStep !== undefined) ? undefined
            : runtimeState.committedStepIndex === 0 && pendingStep === undefined ? 'overview'
                : choreography && completedView !== undefined ? completedView
                : choreography ? choreography.endViews[runtimeState.committedStepIndex] ?? 'overview'
                : draftBooleanRod !== undefined ? toPrimitiveCameraViewId(scope, 'boolean-junction')
                    : plan.viewByStepIndex[runtimeState.pendingStepIndex ?? runtimeState.committedStepIndex] ?? 'overview');
        const guidedBase = withPrimitiveCameraDestination(geometryBase, graph, activeView, camera);
        const base = {
            ...guidedBase,
            spaceRenderOptions: { ...guidedBase.spaceRenderOptions, camera: {
                ...guidedBase.spaceRenderOptions?.camera,
                initializationKey: runtimeState.conceptArrangement
                    ? `primitive:concept:${primitiveConceptLayoutKey(runtimeState.conceptArrangement)}`
                    : `primitive:${draft.operation}`,
            } },
        };

        if (assignment !== undefined && draftBooleanRod !== undefined) {
            return {
                base,
                transitionPhases: derivePrimitiveStepTransitionPhases({
                    spaceKey,
                    config,
                    previousWheels: draftWheels,
                    previousBooleanRod: draftBooleanRod,
                    step: deriveBooleanAssignmentStep({
                        config,
                        previousDraft: assignment.previousDraft,
                        nextDraft: assignment.nextDraft,
                        inputRole: assignment.inputRole,
                    }),
                }),
            };
        }

        const deriveConceptGuidance = () => {
            const playback = runtimeState.episode!;
            const wheels = steps[0]?.keyframes.at(-1)?.wheels;
            if (!conceptBeat || !wheels) throw new Error('Concept guidance lost its beat arrangement.');
            return derivePrimitiveConceptGuidance({ beat: conceptBeat, playback, wheels,
                frame: buildPrimitiveRodFrame({ spaceKey, config, wheels }), emphasis: config.base.display.emphasis });
        };
        const guidance = resolvedPlan && runtimeState.episode ? derivePrimitiveEpisodeGuidance({
            plan: resolvedPlan.plan, replay: resolvedPlan.replay, playback: runtimeState.episode,
            openingStepIndex: resolvedPlan.openingStepIndex,
            committedStepIndex: runtimeState.committedStepIndex, steps,
            frameAt: index => buildPrimitiveRodFrame({ spaceKey, config, wheels: toPrimitiveStepWheels(steps[index]!),
                booleanRod: steps[index]!.keyframes.at(-1)!.booleanRod }),
            emphasis: config.base.display.emphasis,
        }) : runtimeState.episode && runtimeState.conceptArrangement ? deriveConceptGuidance() : undefined;

        if (pendingStep === undefined) return { base, ...(guidance ? { guidance } : {}) };

        const reversing = runtimeState.pendingStepIndex! < runtimeState.committedStepIndex;
        const playback = runtimeState.episode;
        const scene = playback
            ? resolvePrimitiveEpisode(playback.episodeHandle, runtimeState.sceneIndex ?? 0).scene : undefined;
        const sceneBeat = playback && scene?.sceneKind === 'trace' ? scene.beats[playback.beatIndex] : undefined;
        const rateCue = sceneBeat?.animationRateAfterCue === undefined ? undefined
            : sceneBeat.cues?.find(cue => cue.id === sceneBeat.animationRateAfterCue);
        const rateCueIndex = rateCue === undefined ? -1 : sceneBeat!.cues!.indexOf(rateCue);
        const rated = !reversing && sceneBeat !== undefined && (playback!.cueIndex ?? 0) > rateCueIndex;
        // A deferred rate marks an accelerated continuation: the camera holds while the rate ramps up.
        const holdCamera = rated && rateCue !== undefined;
        const rampOrigin = holdCamera && resolvedPlan ? planAnchorIndex(resolvedPlan.replay, rateCue!) : undefined;
        const rateAt = (index: number) => {
            if (reversing) return config.base.animation.rewindRate ?? 1;
            const peak = rated ? sceneBeat!.animationRate ?? 1 : 1;
            const ramp = sceneBeat?.animationRateRampSteps;
            if (ramp === undefined || rampOrigin === undefined) return peak;
            return Math.pow(peak, Math.min(1, Math.max(0, index - rampOrigin) / ramp));
        };
        const retime = (phases: ReturnType<typeof derivePrimitiveStepTransitionPhases>, rate: number) => rate === 1 ? phases : phases.map(phase => {
            const timed = { ...phase };
            timed.transitionOptions = { ...phase.transitionOptions };
            if (timed.transitionOptions.transitionKind === 'interpolated') timed.transitionOptions.duration /= rate;
            else if (timed.transitionOptions.delay !== undefined) timed.transitionOptions.delay /= rate;
            return timed;
        });
        const arrivalAt = (index: number) => resolvePrimitivePresentationCameraView(
            resolvedPlan && runtimeState.episode && cameraPolicy
            ? selectEpisodeArrivalCameraView({
                beat: resolvedPlan.plan.beats[runtimeState.episode.beatIndex], replay: resolvedPlan.replay,
                pendingStepIndex: index, policy: cameraPolicy,
            }) : undefined, scope, graph);
        const arrivalView = arrivalAt(runtimeState.pendingStepIndex!);
        const first = runtimeState.episode && runtimeState.episodeVisualStepIndex !== undefined
            && runtimeState.pendingStepIndex! > runtimeState.committedStepIndex
            ? runtimeState.committedStepIndex + 1 : runtimeState.pendingStepIndex!;
        const choreographedPhases = steps.slice(first, runtimeState.pendingStepIndex! + 1).flatMap((step, offset) => {
            const index = first + offset;
            const previous = offset === 0 ? committedStep : steps[index - 1];
            const previousWheels = previous === undefined ? [] : toPrimitiveStepWheels(previous);
            const booleanRod = previous?.keyframes.at(-1)?.booleanRod;
            const phases = derivePrimitiveStepTransitionPhases({
                spaceKey, config, previousWheels,
                ...(booleanRod === undefined ? {} : { previousBooleanRod: booleanRod }),
                step: reversing
                    ? reversePrimitiveStepStates(steps, runtimeState.committedStepIndex, runtimeState.pendingStepIndex!)
                    : step,
            });
            const stepArrivalView = arrivalAt(index);
            if (!reversing && step.illustrativeQuotientPower !== undefined) {
                const view = toPrimitiveCameraViewId(scope, `illustrative-quotient:${step.illustrativeQuotientPower}`);
                const openingNavigation = deriveCameraGraphNavigationPhase(
                    withPrimitiveCameraDestination(base, graph, `${view}:opening`, camera), undefined,
                );
                const navigation = deriveCameraGraphNavigationPhase(
                    withPrimitiveCameraDestination(base, graph, view, camera), undefined,
                );
                if (!navigation || !openingNavigation) throw new Error('Quotient illustration requires guided camera navigation.');
                const expansion = step.keyframes.findIndex(frame => frame.wheels.some(wheel =>
                    wheel.role === 'quotient' && wheel.powers.includes(step.illustrativeQuotientPower!)));
                if (expansion < 1) throw new Error('Sampled Quotient cycle requires an expansion after its starting state.');
                const expansionKey = `${step.stepKey}:${expansion}`;
                const rate = rateAt(index);
                const framed = retime(phases, rate).flatMap((phase, at) => {
                    const timed = { ...phase };
                    if (at === phases.length - 1 && timed.transitionOptions.transitionKind === 'instant') {
                        timed.transitionOptions = { ...timed.transitionOptions, delay: 450 };
                    }
                    if (timed.transitionOptions.transitionKey === expansionKey) {
                        if (rate === 1 && timed.transitionOptions.transitionKind === 'interpolated') {
                            timed.cameraNavigation = navigation.cameraNavigation;
                        } else return [navigation, timed];
                    }
                    return [timed];
                });
                framed.splice(1, 0, openingNavigation, {
                    patch: {}, transitionOptions: { transitionKind: 'instant',
                        transitionKey: `${step.stepKey}:skip-hold`, delay: 450 },
                });
                return framed;
            }
            return retime(choreography && !reversing && !holdCamera ? withNaturalCameraChoreography({
                directionCatalog: runtimeState.episode ? runtimeState.sceneDirectionCatalogs?.visuals : undefined,
                base, step, phases, actions: choreography.actions[index]!.map(action =>
                    stepArrivalView === undefined ? action : { ...action, view: stepArrivalView }), camera, config, previousWheels,
            }) : phases, rateAt(index));
        });
        if (holdCamera && runtimeState.committedStepIndex === rampOrigin && authoredView !== undefined
            && camera.base.navigationMode === 'guided') {
            const navigation = deriveCameraGraphNavigationPhase(
                withPrimitiveCameraDestination(base, graph, authoredView, camera), undefined,
            );
            if (!navigation) throw new Error('Accelerated continuation requires its authored camera destination.');
            choreographedPhases.unshift(navigation);
        }
        const completionView = selectEpisodeCompletionCameraView({
            atCompletion: runtimeState.pendingStepIndex === steps.length - 1, policy: cameraPolicy, authoredView, arrivalView,
        });
        if (choreography && camera.base.navigationMode === 'guided' && completionView !== undefined && !reversing && !holdCamera) {
            const navigation = deriveCameraGraphNavigationPhase(
                withPrimitiveCameraDestination(base, graph, completionView, camera), undefined,
            );
            if (!navigation) throw new Error('Natural completion requires an Overview camera destination.');
            choreographedPhases.push(...retime([{
                patch: {},
                transitionOptions: { transitionKind: 'instant', transitionKey: `${pendingStep.stepKey}:completion-hold`,
                    delay: camera.natural.dispatchHoldDurationMs },
            }, navigation], rateAt(runtimeState.pendingStepIndex!)));
        }
        // Rewinds run at their own rate and, inside episodes, without emphasis.
        return { base, transitionPhases: choreographedPhases, ...(guidance && !(reversing && playback) ? { guidance } : {}) };
    };
}
